import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import LogoWhite from '../../../../../../assets/images/logos/white-bg-logo-hiacuity.png';
import {
  BriefcaseBusinessIcon,
  ClockIcons,
  MapPinIcons,
  OfficeIcons,
} from '../../../../../../icons/icons';
import { Button } from '../../../../../../ui';
import { ensureValidDate, formatDate } from '../../../../../../utils/helpers/dateUtils';
import {
  IJobPostingPreview,
  useJobPostingPreview,
} from './Hooks/UseJobPostingPreview';

export const JobPostingPreview = (props: IJobPostingPreview) => {
  const { jobPostingData, jobPostId, hidePublishBanner } = props;
  const { t } = useTranslation(NS.adminPages);
  const {
    createPost,
    url,
    videoRef,
    handlePlayPause,
    isPlaying,
    companyLogo,
    isCreatingPost,
    previewHtml,
  } = useJobPostingPreview(props);
  const formatDateString = (dateValue: any): string => {
    const formatted = formatDate(dateValue, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return formatted || 'TBD';
  };

  const calculateDaysLeft = (): string => {
    const endDate = ensureValidDate(jobPostingData.endDate);
    if (!endDate) return 'TBD';

    const currentDate = new Date();

    // Reset time to midnight for accurate day calculation
    endDate.setHours(23, 59, 59, 999);
    currentDate.setHours(0, 0, 0, 0);

    const timeDiff = endDate.getTime() - currentDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.expired);
    } else if (daysDiff === 0) {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.expiresToday);
    } else if (daysDiff === 1) {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.oneDayLeft);
    } else {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.daysLeftCount, { count: daysDiff });
    }
  };
  const keyInfo = [
    {
      key: 'department',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.department),
      value: jobPostingData.department,
      icon: OfficeIcons,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      key: 'location',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.location),
      value: jobPostingData.location,
      icon: MapPinIcons,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      key: 'workType',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.workType),
      value: jobPostingData.workType,
      icon: BriefcaseBusinessIcon,
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      key: 'timeline',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.daysLeftLabel),
      value: `${calculateDaysLeft()} - ${formatDateString(
        jobPostingData.startDate ?? '',
      )} - ${formatDateString(jobPostingData.endDate ?? '')}`,
      icon: ClockIcons,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ];
  return (
    <div className='min-h-screen bg-slate-50'>
      { /* Job Header */ }
      <div className='border-b border-slate-200 bg-white shadow-sm'>
        <div className='mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex min-w-0 items-center gap-3'>
              <div className='flex h-12 min-w-12 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 shadow-sm'>
                <img
                    src={companyLogo || LogoWhite}
                    alt='Company Logo'
                    className='max-h-8 max-w-[150px] object-contain'
                />
              </div>
              <div className='min-w-0'>
                <div className='mb-1 flex flex-wrap items-center gap-2'>
                  <span className='inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700'>
                    <span className='mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500' />
                    { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.badge) }
                  </span>
                </div>
                <h1 className='truncate text-xl font-extrabold text-slate-950'>
                  { jobPostingData.jobTitle }
                </h1>
              </div>
            </div>

            { /* Compact Deadline Info */ }
            <div className='flex w-fit items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm'>
              <ClockIcons className='h-4 w-4 text-emerald-600' />
              <div>
                <span className='font-bold text-emerald-800'>
                  { calculateDaysLeft() }
                </span>
                <span className='ml-1 text-emerald-700'>{ t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.toApply) }</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8'>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]'>
          { /* Left Column - Main Content */ }
          { /* <div className="grid grid-cols-1 lg:grid-cols-3 gap-8"> */ }
          { /* Main Content */ }
          <div className='space-y-4'>
            { /* Job Info Cards */ }
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              { keyInfo.map((item) => (
                <div
                    key={item.key}
                    className='rounded-lg border border-slate-200 bg-white p-3 shadow-sm'
                >
                  <div className='flex items-center gap-3 '>
                    <div
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${item.bgColor}`}
                    >
                      <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                    </div>
                    <div>
                      <div className='text-xs font-semibold text-slate-500'>
                        { item.label }
                      </div>
                      <div
                          className={`${
                            item.key === 'timeline' ? 'text-xs' : 'text-sm'
                          } font-bold text-slate-900`}
                      >
                        { item.value }
                      </div>
                    </div>
                  </div>
                </div>
              )) }
            </div>

            { /* Job Description */ }
            <div className='rounded-lg border border-slate-200 bg-white p-5 shadow-sm'>
              <h2 className='mb-4 text-xl font-extrabold text-slate-900'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.jobDescription) }
              </h2>
              <div
                  className='prose prose-sm max-w-none text-slate-700
                            [&_h1]:mb-3 [&_h1]:mt-5 [&_h1]:text-xl [&_h1]:font-extrabold [&_h1]:text-slate-900
                            [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-900
                            [&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-slate-800
                            [&_p]:mb-3 [&_p]:leading-6
                            [&_ul]:mb-3 [&_ul]:mt-1 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5
                            [&_li]:leading-6
                            [&_strong]:font-bold [&_strong]:text-slate-900'
                  dangerouslySetInnerHTML={{
                    __html: previewHtml,
                  }}
              />
            </div>
          </div>
          { /* Sidebar */ }
          <div className='space-y-4'>
            { /* Company Card */ }
            <div className='sticky top-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm'>
              { url && (
                <div className='mb-6'>
                  { jobPostingData.videoTitle && (
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      { jobPostingData.videoTitle }
                    </h3>
                  ) }
                  { jobPostingData.videoDescription && (
                    <p className='text-sm text-gray-600 mb-4'>
                      { jobPostingData.videoDescription }
                    </p>
                  ) }
                  <div className='bg-gray-50 rounded-xl p-4 border border-gray-100'>
                    <video
                        ref={videoRef}
                        className='w-full rounded-lg mb-3 shadow-sm'
                        controls
                        poster='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRkJGREZGIi8+CjxwYXRoIGQ9Ik0xNDUgNzJMMTc1IDkwTDE0NSAxMDhWNzJaIiBmaWxsPSIjMDU5NjY5Ii8+Cjx0ZXh0IHg9IjE2MCIgeT0iMTMwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2QjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlZpZGVvIFByZXZpZXc8L3RleHQ+Cjwvc3ZnPg=='
                    >
                      <source src={url} type='video/mp4' />
                      { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.browserNotSupported) }
                    </video>
                    <button
                        onClick={handlePlayPause}
                        className='w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md'
                    >
                      <span className='flex items-center justify-center gap-2'>
                        { isPlaying ? (
                          <>
                            <svg
                                className='w-4 h-4'
                                fill='currentColor'
                                viewBox='0 0 24 24'
                            >
                              <path d='M6 4h4v16H6V4zm8 0h4v16h-4V4z' />
                            </svg>
                            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.pause) }
                          </>
                        ) : (
                          <>
                            <svg
                                className='w-4 h-4'
                                fill='currentColor'
                                viewBox='0 0 24 24'
                            >
                              <path d='M8 5v14l11-7z' />
                            </svg>
                            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.play) }
                          </>
                        ) }
                      </span>
                    </button>
                  </div>
                </div>
              ) }

              { /* Apply Section */ }
              <div className='mb-4 rounded-lg bg-emerald-600 p-5 text-center shadow-sm'>
                <h3 className='text-white font-bold text-xl mb-2'>
                  { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.readyToApply) }
                </h3>
                <p className='text-emerald-100 text-sm mb-4'>
                  { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.joinTeam) }
                </p>
                <Button
                    onClick={() => {}}
                    variant='primary'
                    label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.applyNow)}
                    className='w-full  text-emerald-600 hover:bg-emerald-50 font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                />
              </div>

              { /* Share Section */ }
              <div className=''>
                { /* URL Display when job post is created */ }

                <div className='text-center mb-6'>
                  <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                    { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.shareOpportunity) }
                  </h3>
                  <p className='text-sm text-gray-600'>
                    { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.helpFindCandidate) }
                  </p>
                </div>

                <div className='space-y-4'>
                  { jobPostId && (
                    <div className='relative group'>
                      <div className='flex items-stretch gap-0 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200'>
                        <div className='flex items-center flex-1 px-4 py-3 bg-white/80 backdrop-blur-sm'>
                          <svg
                              className='w-4 h-4 text-emerald-600 mr-2 flex-shrink-0'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
                            />
                          </svg>
                          <input
                              type='text'
                              value={`${window.location.origin}/jobs/${jobPostId}`}
                              readOnly
                              className='flex-1 bg-transparent text-sm text-gray-700 focus:outline-none font-mono'
                          />
                        </div>
                      </div>
                    </div>
                  ) }

                  { /* Action Buttons */ }
                  <div className='grid grid-cols-2 gap-3'>
                    <Button
                        label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.copyUrl)}
                        hasIcon
                        className='w-full'
                        variant='secondary'
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `${window.location.origin}/jobs/${jobPostId}`,
                          );
                          toast.success(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.urlCopied), {
                            position: 'bottom-center',
                          });
                        }}
                        icon={
                          <svg
                              className='w-4 h-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                            />
                          </svg>
                        }
                    />
                    <Button
                        label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.emailFriend)}
                        hasIcon
                        variant='primary'
                        onClick={() => {}}
                        className='w-full'
                        icon={
                          <svg
                              className='w-4 h-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                            />
                          </svg>
                        }
                    />
                  </div>
                  { /* Social Media */ }
                  <div className='border-t border-gray-100 pt-4'>
                    <p className='text-xs text-gray-500 text-center mb-3 uppercase tracking-wide font-medium'>
                      { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.socialNetworks) }
                    </p>
                    <div className='grid grid-cols-2 gap-3'>
                      <button
                          onClick={() => {}}
                          className='flex items-center gap-3 px-3 py-3 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group'
                      >
                        <svg
                            className='w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
                        </svg>
                        <span className='text-sm font-medium text-gray-700 group-hover:text-blue-600'>
                          LinkedIn
                        </span>
                      </button>
                      <button
                          onClick={() => {}}
                          className='flex items-center gap-3 px-3 py-3 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all duration-200 group'
                      >
                        <svg
                            className='w-5 h-5 text-green-600 group-hover:scale-110 transition-transform'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488' />
                        </svg>
                        <span className='text-sm font-medium text-gray-700 group-hover:text-green-600'>
                          WhatsApp
                        </span>
                      </button>
                      <button
                          onClick={() => {}}
                          className='flex items-center gap-3 px-3 py-3 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group'
                      >
                        <svg
                            className='w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                        </svg>
                        <span className='text-sm font-medium text-gray-700 group-hover:text-blue-600'>
                          Facebook
                        </span>
                      </button>
                      <button
                          onClick={() => {}}
                          className='flex items-center gap-3 px-3 py-3 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group'
                      >
                        <svg
                            className='w-5 h-5 text-gray-700 group-hover:scale-110 transition-transform'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
                        </svg>
                        <span className='text-sm font-medium text-gray-700 group-hover:text-gray-600'>
                          X
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          { /* </div> */ }
        </div>
      </div>

      {!hidePublishBanner && (
        <div className='mx-auto max-w-6xl px-4 pb-6 sm:px-6 lg:px-8'>
          <div className='flex flex-col gap-3 rounded-lg border border-emerald-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <h3 className='text-base font-extrabold text-slate-900'>
                { jobPostId ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateJobPost) : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishJob) }
              </h3>
              <p className='text-sm text-slate-500'>
                { jobPostId
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateSubtitle)
                  : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishSubtitle) }
              </p>
            </div>
            <Button
                onClick={createPost}
                variant='primary'
                label={
                  isCreatingPost
                    ? jobPostId
                      ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updating)
                      : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.creating)
                    : jobPostId
                      ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateButton)
                      : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.createButton)
                }
                className='w-full sm:w-auto'
                disabled={
                  isCreatingPost ||
                !jobPostingData.jobTitle ||
                !jobPostingData.department ||
                !jobPostingData.location
                }
            />
          </div>
        </div>
      )}
    </div>
  );
};
